import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const EditableTextCell = ({ value, onSave, type = 'text', options = null, placeholder = '', className = '', prefix = '', decimals = null }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 220, openUp: false });
  const cellRef = useRef(null);

  useEffect(() => { setCurrentValue(value); }, [value]);

  useEffect(() => {
    if (!isEditing || !options) return;
    const handleClickOutside = (e) => {
      if (
        cellRef.current && !cellRef.current.contains(e.target) &&
        !(e.target.closest && e.target.closest('[data-editablecell-dropdown]'))
      ) {
        setIsEditing(false);
      }
    };
    const handleReposition = (e) => {
      if (e.target && e.target.closest && e.target.closest('[data-editablecell-dropdown]')) return;
      setIsEditing(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isEditing, options]);

  const handleSave = async (newVal) => {
    try {
      const success = await onSave(newVal);
      if (!success) setCurrentValue(value);
      setIsEditing(false);
    } catch (error) {
      setCurrentValue(value);
      setIsEditing(false);
    }
  };

  const handleOpen = () => {
    if (options && cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 220 && rect.top > 220;
      setCoords({
        top: openUp ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 200),
        openUp,
      });
    }
    setIsEditing(true);
  };

  const isNumeric = type === 'number';

  if (isEditing) {
    if (options) {
      return (
        <div className="relative" ref={cellRef}>
          <div className="w-full px-2 py-1 text-sm border border-blue-400 rounded bg-white text-gray-800">
            {currentValue || 'Seleccionar...'}
          </div>
          {createPortal(
            <div
              data-editablecell-dropdown
              className="fixed z-[10000] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
              style={{
                left: coords.left,
                width: coords.width,
                maxWidth: 320,
                ...(coords.openUp ? { bottom: window.innerHeight - coords.top } : { top: coords.top }),
              }}
            >
              <div className="max-h-[220px] overflow-y-auto overflow-x-hidden slim-scrollbar py-1">
                <button
                  type="button"
                  onMouseDown={e => { e.preventDefault(); handleSave(''); }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  Seleccionar...
                </button>
                {options.map(opt => {
                  const val = opt.value ?? opt;
                  const label = opt.label ?? opt;
                  const isSelected = currentValue === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      onMouseDown={e => { e.preventDefault(); handleSave(val); }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        isSelected
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body
          )}
        </div>
      );
    }
    return (
      <input
        type={type}
        value={currentValue || ''}
        onChange={e => setCurrentValue(e.target.value)}
        onBlur={() => handleSave(currentValue)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave(currentValue);
          if (e.key === 'Escape') { setCurrentValue(value); setIsEditing(false); }
        }}
        autoFocus
        placeholder={placeholder}
        className={`w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 ${isNumeric ? 'font-mono text-right' : ''}`}
      />
    );
  }

  const displayValue = isNumeric && value != null
    ? `${prefix}${Number(value).toLocaleString('de-DE', decimals != null ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals } : undefined)}`
    : (value ? `${prefix}${value}` : '—');

  return (
    <div
      ref={cellRef}
      onDoubleClick={handleOpen}
      className={`cursor-pointer hover:bg-gray-50 px-1 -mx-1 rounded transition-colors w-full min-h-[24px] ${isNumeric ? 'font-mono text-sm tabular-nums' : 'text-sm'} ${className}`}
      title="Doble click para editar"
    >
      {displayValue}
    </div>
  );
};

export default EditableTextCell;
